import { Instant } from "@js-joda/core";
import {
	type ChangeEvent,
	type FC,
	useEffect,
	useId,
	useMemo,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
	MyBusinessStarMap,
	Platform,
	type UnifiedReviewDto,
	type UnifiedReviewStars,
} from "shared";
import { useEventCallback } from "usehooks-ts";
import { Icon, IconSVG } from "../common/Icon";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { TimeFormatter, useTime } from "../common/time/useTime";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { useMediaActions } from "../content/useMediaActions";
import { LinkGen } from "../LinkGen";
import { useUnifiedReviewsActions } from "../settings/useIntegrations";
import "./Review.css";

type StarsProps = { stars: UnifiedReviewStars };

const ReviewStars: FC<Readonly<StarsProps>> = ({ stars }) => {
	const starnumber = MyBusinessStarMap[stars];
	const id = useId();

	return (
		<div className="stars">
			{starnumber > 0
				? Array.from({ length: starnumber }, (_, idx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: prefixed with useId
						<Icon key={`${id}-star-${idx}`} fill name="star" size={24} />
					))
				: "No Star Rating"}
		</div>
	);
};

type Props = { review: UnifiedReviewDto; locationId?: string };

export const UnifiedReviewCard: FC<Readonly<Props>> = ({
	review,
	locationId,
}) => {
	const publishedReply = review.reviewReply.comment ?? "";
	const pendingDraft =
		!publishedReply && review.draftReply?.status === "pending_approval"
			? review.draftReply.comment
			: "";
	const [reply, setReply] = useState(publishedReply || pendingDraft);
	const [editing, setEditing] = useState(false);
	const { format } = useTime();
	const navigate = useNavigate();
	const { generateV2, saveGeneratedV2, invalidateMedia } = useMediaActions();
	const { reply: replyMutation, generateReply } =
		useUnifiedReviewsActions(locationId);

	const isChanging = replyMutation.isPending || generateReply.isPending;

	const { comment, starRating, reviewer, platform, canReply } = review;
	const { name, anonymous, photoUrl } = reviewer;
	const hasReply = publishedReply.length > 0;

	useEffect(() => {
		if (!editing) {
			setReply(publishedReply || pendingDraft);
		}
	}, [editing, pendingDraft, publishedReply]);

	const createdAt = useMemo(() => Instant.parse(review.createdAt), [review]);
	const updatedAt = useMemo(() => Instant.parse(review.updatedAt), [review]);

	const platformLabel = platform === "facebook" ? "Facebook" : "Google";

	const handleReplyChange = useEventCallback(
		(e: ChangeEvent<HTMLTextAreaElement>) => {
			setReply(e.target.value);
		},
	);

	const handleGenerateReply = useEventCallback(async () => {
		const { reply: generated } = await generateReply.mutateAsync(review.id);
		setReply(generated);
	});

	const handleUpdateReply = useEventCallback(async () => {
		await replyMutation.mutateAsync({
			reviewId: review.id,
			comment: reply,
		});
		setEditing(false);
	});

	const handleCreatePost = useEventCallback(async () => {
		const { prompt, caption, postPlatform } = buildReviewToPost(review);

		const result = await generateV2.mutateAsync({
			prompt,
			aspectRatio: "1:1",
		});

		const saved = await saveGeneratedV2.mutateAsync(result.imageUrl);
		await invalidateMedia();

		navigate(
			`${LinkGen.PostCreate()}?mediaId=${saved.mediaId}&platform=${postPlatform}`,
			{ state: { generatedContent: caption } },
		);
	});

	const canCreatePost =
		!!comment?.trim() ||
		(platform === "facebook" &&
			(review.recommendationType === "positive" ||
				review.recommendationType === "negative"));

	return (
		<Card className="review-card">
			<Card.Header>
				<Avatar
					imgUrl={photoUrl}
					imgAlt={anonymous ? "Anonymous" : name}
					imgLazy
				>
					<Avatar.Body>
						<h3>{reviewer.name}</h3>
						<span style={{ fontSize: 12, color: "var(--bs-secondary-color)" }}>
							{platformLabel}
							{review.recommendationType
								? ` · ${review.recommendationType}`
								: ""}
						</span>
					</Avatar.Body>
				</Avatar>
				<aside>
					Created:{" "}
					<time dateTime={format(TimeFormatter.DateTime, createdAt)}>
						{format(TimeFormatter.DateTime, createdAt)}
					</time>
				</aside>
			</Card.Header>
			<Card.Body>
				<div className="bar">
					<ReviewStars stars={starRating} />
					<p>
						Latest update:{" "}
						<time dateTime={format(TimeFormatter.DateTime, updatedAt)}>
							{format(TimeFormatter.DateTime, updatedAt)}
						</time>
					</p>
				</div>
				{comment && (
					<blockquote>
						<p>{comment}</p>
					</blockquote>
				)}
			</Card.Body>
			<Card.Footer>
				{editing && canReply && (
					<ExtraForm.Group label="Reply">
						<ExtraForm.Control
							disabled={isChanging}
							as={"textarea"}
							value={reply}
							onChange={handleReplyChange}
							rows={3}
							placeholder="Reply to review..."
						/>
					</ExtraForm.Group>
				)}
				{!editing && hasReply && (
					<>
						<h4>Reply:</h4>
						<blockquote>
							<p>{reply}</p>
						</blockquote>
					</>
				)}
				{!editing && !hasReply && pendingDraft && (
					<>
						<h4>Draft reply</h4>
						<p
							style={{
								margin: "0 0 8px",
								fontSize: 12,
								color: "var(--bs-secondary-color)",
							}}
						>
							Waiting for approval. Send it, or edit it first.
						</p>
						<blockquote>
							<p>{pendingDraft}</p>
						</blockquote>
					</>
				)}
				<div className="bar">
					{editing && canReply ? (
						<>
							<Button
								className="review-control"
								icon="reply"
								iconSize={24}
								disabled={reply.length === 0 || isChanging}
								loading={replyMutation.isPending}
								onClick={handleUpdateReply}
							>
								{hasReply ? "Update" : "Reply"}
							</Button>
							<Button
								className="review-control"
								disabled={reply.length > 0 || isChanging}
								icon={IconSVG.Sparkle}
								iconSize={24}
								onClick={handleGenerateReply}
								loading={generateReply.isPending}
							>
								Generate
							</Button>
							<Button
								className="review-control"
								icon="cancel"
								iconSize={24}
								onClick={() => setEditing(false)}
								disabled={isChanging}
							>
								Cancel
							</Button>
						</>
					) : (
						<>
							{canReply && pendingDraft && (
								<Button
									className="review-control"
									icon="reply"
									iconSize={24}
									loading={replyMutation.isPending}
									disabled={isChanging}
									onClick={handleUpdateReply}
								>
									Send
								</Button>
							)}
							{canReply && (
								<Button
									className="review-control"
									icon="edit"
									iconSize={24}
									onClick={() => setEditing(true)}
								>
									{hasReply ? "Edit" : pendingDraft ? "Edit" : "Reply"}
								</Button>
							)}
							<Button
								className="review-control"
								icon="add"
								iconSize={24}
								onClick={handleCreatePost}
								loading={generateV2.isPending || saveGeneratedV2.isPending}
								disabled={
									!canCreatePost ||
									isChanging ||
									generateV2.isPending ||
									saveGeneratedV2.isPending
								}
							>
								Create Post
							</Button>
						</>
					)}
				</div>
			</Card.Footer>
		</Card>
	);
};

function buildReviewToPost(review: UnifiedReviewDto): {
	prompt: string;
	caption: string;
	postPlatform: Platform;
} {
	const name = review.reviewer.name?.trim() || "a customer";
	const quote = review.comment?.trim() ?? "";
	const stars = MyBusinessStarMap[review.starRating];
	const isFacebook = review.platform === "facebook";

	if (isFacebook) {
		const recommendation =
			review.recommendationType === "negative" ? "critical" : "positive";
		const quoteLine = quote
			? `The recommendation says: "${quote}".`
			: `${name} left a ${recommendation} Facebook recommendation (no written review text).`;
		const prompt = `Create a professional social media graphic for a Facebook customer recommendation. Style it as a warm testimonial card suitable for Facebook. Attribution: ${name}. ${quoteLine} Use an elegant, modern design with good contrast and readability. Do not invent fake star ratings unless the quote mentions them.`;
		const caption = quote
			? `We're grateful for this Facebook recommendation from ${name}!\n\n"${quote}"\n\nThank you for trusting us — it means a lot to our team and community.`
			: `We're grateful for this Facebook recommendation from ${name}!\n\nThank you for trusting us — it means a lot to our team and community.`;
		return {
			prompt,
			caption,
			postPlatform: Platform.FACEBOOK,
		};
	}

	const starText = stars > 0 ? `${stars}-star review` : "review";
	const prompt = `Create a professional social media graphic for a customer testimonial. Include a ${starText} rating visual (${stars} gold stars). The quote is: "${quote}". Attribution: ${name}. Use an elegant, modern design with good contrast and readability. Make it suitable for Instagram or Facebook.`;
	const caption = quote
		? `We're grateful for this ${starText} from ${name}!\n\n"${quote}"\n\nThank you for trusting us — it means a lot to our team and community.`
		: `We're grateful for this ${starText} from ${name}!\n\nThank you for trusting us — it means a lot to our team and community.`;
	return {
		prompt,
		caption,
		postPlatform: Platform.GOOGLE_BUSINESS,
	};
}
